'use client'

import { useState, useEffect, useRef, type RefObject } from 'react'
import { usePathname } from 'next/navigation'
import MobileHeader from './mobile-header'
import DesktopHeader from './desktop-header'
import { useLocale } from '~/contexts/LocaleContext'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useLocalizedNavigation } from '~/hooks/use-localized-navigation'

// Create a client
const queryClient = new QueryClient()

export default function Header() {
  const pathname = usePathname()
  const { locale } = useLocale()
  // Shared state
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMainMenuOpen, setIsMainMenuOpen] = useState(false)
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false)
  const langMenuRef = useRef<HTMLDivElement>(null) as React.MutableRefObject<HTMLDivElement>

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Handle click outside language menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (langMenuRef.current && !langMenuRef.current.contains(event.target as Node)) {
        setIsLangMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Toggle functions
  const toggleMainMenu = () => {
    setIsMainMenuOpen(!isMainMenuOpen)
  }

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen)
  }

  return (
    <QueryClientProvider client={queryClient}>
      <HeaderContent
        pathname={pathname}
        locale={locale}
        isScrolled={isScrolled}
        isMainMenuOpen={isMainMenuOpen}
        toggleMainMenu={toggleMainMenu}
        isSidebarOpen={isSidebarOpen}
        toggleSidebar={toggleSidebar}
        isLangMenuOpen={isLangMenuOpen}
        setIsLangMenuOpen={setIsLangMenuOpen}
        langMenuRef={langMenuRef}
      />
    </QueryClientProvider>
  )
}

function HeaderContent({
  pathname,
  locale,
  isScrolled,
  isMainMenuOpen,
  toggleMainMenu,
  isSidebarOpen,
  toggleSidebar,
  isLangMenuOpen,
  setIsLangMenuOpen,
  langMenuRef,
}: {
  pathname: string
  locale: string
  isScrolled: boolean
  isMainMenuOpen: boolean
  toggleMainMenu: () => void
  isSidebarOpen: boolean
  toggleSidebar: () => void
  isLangMenuOpen: boolean
  setIsLangMenuOpen: (isOpen: boolean) => void
  langMenuRef: React.RefObject<HTMLDivElement>
}) {
  // Determine active state
  const isActiveState = isScrolled || isMainMenuOpen || isSidebarOpen || true

  return (
    <header
      className={`px-4 py-3 md:px-8 md:py-4 ${
        isActiveState
          ? 'border-b border-[#CED0D3] bg-white py-3 md:py-4'
          : 'bg-transparent py-3 md:py-4'
      }`}
    >
      {/* Mobile Header (visible on small screens) */}
      <MobileHeader
        isActive={isActiveState}
        isMainMenuOpen={isMainMenuOpen}
        toggleMainMenu={toggleMainMenu}
        pathname={pathname}
        isLangMenuOpen={isLangMenuOpen}
        setIsLangMenuOpen={setIsLangMenuOpen}
        isScrolled={isScrolled}
        langMenuRef={langMenuRef}
      />

      {/* Desktop Header (visible on medium screens and larger) */}
      <DesktopHeader
        isActive={isActiveState}
        isSidebarOpen={isSidebarOpen}
        toggleSidebar={toggleSidebar}
        isLangMenuOpen={isLangMenuOpen}
        setIsLangMenuOpen={setIsLangMenuOpen}
        isScrolled={isScrolled}
        langMenuRef={langMenuRef}
      />
    </header>
  )
}
